import {
  ValidateNested,
  IsDefined,
  IsString,
  IsArray,
  IsEnum,
  IsOptional,
  IsIn,
  IsBoolean,
} from 'class-validator';
import { Type } from 'class-transformer';
import { NewFlatContactDTO } from './FlatContactDTOs';
import { IsCuid } from 'lib/validation/IsCuid';
import { GetEndpoint, PostEndpoint, PutEndpoint } from 'types/Endpoint';
import {
  CommunicationLinkStatus,
  EngageFlatsFilterStatus,
  EngagePropertiesSortDirection,
  EngagePropertiesSortField,
} from 'types/Engage';

enum FlatOccupationStatus {
  OCCUPIED = 'occupied',
  UNOCCUPIED = 'unoccupied',
  UNKNOWN = 'unknown',
}

export type FlatResidentSurvey = {
  status: ResidentSurveyStatus;
  startDate: ISODateString;
  endDate: ISODateString | null;
} | null;

export enum EngageFlatIsHMO {
  UNKNOWN = 'UNKNOWN',
  NO = 'NO',
  YES = 'YES',
}

export enum EngageFlatIsCommerciallyOwned {
  UNKNOWN = 'UNKNOWN',
  NO = 'NO',
  YES = 'YES',
}

enum FlatContactType {
  resident = 'resident',
  residentOwner = 'resident-owner',
  owner = 'owner',
  movedSold = 'moved-sold',
  noAssociation = 'no-association',
  other = 'other',
  lettingAgent = 'letting-agent',
  onBehalfOf = 'on-behalf-of',
}

enum DataSource {
  'engage' = 'engage',
  'app' = 'app',
}

enum ResidentSurveyStatus {
  'pending' = 'pending',
  'ready' = 'ready',
  'in-progress' = 'in-progress',
  'complete' = 'complete',
}

type ISODateString = string & { __brand: 'ISODate' };

enum CommunicationStatus {
  requested = 'requested',
  sent = 'sent',
  failed = 'failed',
}

enum CommunicationType {
  email = 'email',
  letter = 'letter',
}

enum CommunicationAccessedFrom {
  'letterQr' = 'letter-qr',
  'emailLink' = 'email-link',
  'app' = 'app',
  'unknown' = 'unknown',
}

export type EngageCommunication = {
  id: string;
  createdDate: ISODateString;
  name: string;
  type: CommunicationType;
  contactId: string | null;
  address: string;
  status: CommunicationStatus;
  statusDate: ISODateString;

  link: string;
  linkAccessed: {
    from: CommunicationAccessedFrom;
    date: ISODateString;
  } | null;
  linkStatus: CommunicationLinkStatus;
  previewContentURL: string | null;
};

export type EngageContact = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  isPrimary: boolean;
  isOwner: boolean;
  isResident: boolean;
  isUnder16: boolean;
  hasSupportNeeds: boolean;
  hasCognitiveImpairment: boolean;
  hasHearingImpairment: boolean;
  hasMobilityLimitation: boolean;
  hasSocialAnxiety: boolean;
  hasEmergencyEvacuationStatement: boolean;
  hasGivenSupportNeedsConsent: boolean;
  comments: string;
  internalNotes: string;
  contactType: FlatContactType;
  addedTimestamp: ISODateString;
  addedVia: DataSource;
  archivedTimestamp: ISODateString | null;
  archivedVia: DataSource | null;
  isConfirmed: boolean;
  confirmedDate: ISODateString | null;
};

/**
 * Get Properties
 */

export type BlockManagerGetPropertiesRequest = GetEndpoint<{
  url: '/blockmanagement/properties';
  query: {
    page: number;
    search?: string;
    sortBy?: EngagePropertiesSortField;
    sortDirection?: EngagePropertiesSortDirection;
  };
  response: {
    totalCount: number;
    properties: {
      id: string;
      reference: string;
      customReference: string;
      name: string;
      parentName: string;
      addressLines: string[];
      postcode: string;
      organisationId: string;
      engageUrl: string;
      noOfFlats: number;
      noOfContacts: number;
    }[];
    expandedData: {
      organisations: Record<string, { name: string }>;
    };
  };
}>;

/**
 * Onboard Flats
 */

export class BlockManagerOnboardFlatsParams {
  @IsDefined()
  @IsCuid()
  propertyUid!: string;
}

export type BlockManagerOnboardFlatsRequest = PostEndpoint<{
  url: '/blockmanagement/properties/:propertyUid/flats/onboard';
  params: typeof BlockManagerOnboardFlatsParams;
  query: {};
  body: {};
  response: {
    onboarded: number;
    updated: number;
  };
}>;

/**
 * Get Flats
 */

export type BlockManagerGetFlatsRequest = GetEndpoint<{
  url: '/blockmanagement/properties/:propertyUid/flats';
  query: {
    page: number;
    search?: string;
    residentSurveyStatuses?: EngageFlatsFilterStatus[];
    occupationStatuses?: FlatOccupationStatus[];
  };
  response: {
    totalCount: number;
    flats: {
      id: string;
      name: string;
      reference: string;
      customReference: string;
      occupationStatus: FlatOccupationStatus;
      isHMO: EngageFlatIsHMO;
      isCommerciallyOwned: EngageFlatIsCommerciallyOwned;
      isMissingResidents: boolean | null;
      residentSurveyStatus: ResidentSurveyStatus | null;
      contacts: {
        id: string;
        name: string;
        isOwner: boolean;
        isResident: boolean;
        isUnder16: boolean;
        hasSupportNeeds: boolean;
        contactType: FlatContactType;
        isConfirmed: boolean;
        confirmedDate: ISODateString | null;
      }[];
      matchingContactIds?: string[];
    }[];
  };
}>;

/**
 * Get Flat
 */

export type BlockManagerGetFlatRequest = GetEndpoint<{
  url: '/blockmanagement/properties/:propertyUid/flats/:flatUid';
  query: {};
  response: {
    residentSurvey: FlatResidentSurvey;
    contacts: EngageContact[];
    communications: EngageCommunication[];
  };
}>;

/**
 * Get Flat Communications
 */
export type BlockManagerGetFlatCommunicationsRequest = GetEndpoint<{
  url: '/blockmanagement/properties/:propertyUid/flats/:flatUid/communications';
  query: {};
  response: {
    communications: EngageCommunication[];
  };
}>;

/**
 * Send Communication
 */
export class BlockManagerSendCommsParams {
  @IsDefined()
  @IsCuid()
  propertyUid!: string;

  @IsDefined()
  @IsCuid()
  flatUid!: string;
}

export class BlockManagerSendCommsBody {
  @IsDefined()
  @IsString()
  communicationTemplateId!: string;

  // @MinLength(1, { each: true }) // this can be an empty array for letters
  @IsDefined()
  @IsArray()
  @IsCuid({ each: true }) // if present, then must be an array of cuids
  contactIds!: string[];
}

export type BlockManagerSendCommunicationRequest = PostEndpoint<{
  url: '/blockmanagement/properties/:propertyUid/flats/:flatUid/sendcommunication';
  body: typeof BlockManagerSendCommsBody;
  params: typeof BlockManagerSendCommsParams;
  query: {};
  response: {
    communications: EngageCommunication[];
  };
}>;

/**
 * Shared params
 */

export class BlockManagerFlatParams {
  @IsDefined()
  @IsCuid()
  propertyUid!: string;

  @IsDefined()
  @IsCuid()
  flatUid!: string;
}

/**
 * Add Contact
 */

export class BlockManagerAddContactParams extends BlockManagerFlatParams {}

export class BlockManagerAddContactBody {
  constructor(data: BlockManagerAddContactBody) {
    Object.assign(this, data);
  }

  @ValidateNested()
  @Type(() => NewFlatContactDTO)
  @IsDefined()
  contact!: NewFlatContactDTO;
}

export type BlockManagerAddContactRequest = PostEndpoint<{
  url: '/blockmanagement/properties/:propertyUid/flats/:flatUid/contact';
  params: typeof BlockManagerAddContactParams;
  query: {};
  body: typeof BlockManagerAddContactBody;
  response: {
    message: string;
    flatOccupationStatus: FlatOccupationStatus;
    contact: EngageContact;
    residentSurvey: FlatResidentSurvey;
  };
}>;

/**
 * Update Contact
 */

export class BlockManagerContactParams extends BlockManagerFlatParams {
  @IsDefined({ message: 'contactUid is required.' })
  @IsCuid()
  contactUid!: string;
}

export class BlockManagerUpdateContactParams extends BlockManagerContactParams {}

export class BlockManagerUpdateContactBody {
  constructor(data: BlockManagerUpdateContactBody) {
    Object.assign(this, data);
  }

  @ValidateNested()
  @Type(() => NewFlatContactDTO)
  @IsDefined({ message: 'contact is required.' })
  contact!: NewFlatContactDTO;
}

export type BlockManagerUpdateContactRequest = PutEndpoint<{
  url: '/blockmanagement/properties/:propertyUid/flats/:flatUid/contacts/:contactUid';
  params: typeof BlockManagerUpdateContactParams;
  query: {};
  body: typeof BlockManagerUpdateContactBody;
  response: {
    flatOccupationStatus: FlatOccupationStatus;
    residentSurvey: FlatResidentSurvey;
    contact: EngageContact;
  };
}>;

/**
 * Archive / Unarchive Contact
 */

export class BlockManagerArchiveContactParams extends BlockManagerContactParams {
  @IsDefined()
  @IsIn(['archive', 'unarchive'])
  action!: 'archive' | 'unarchive';
}

export type BlockManagerArchiveContactRequest = PostEndpoint<{
  url: '/blockmanagement/properties/:propertyUid/flats/:flatUid/contacts/:contactUid/:action';
  params: typeof BlockManagerArchiveContactParams;
  query: {};
  body: {};
  response: {
    contact: EngageContact | null;
    flatOccupationStatus: FlatOccupationStatus;
    residentSurvey: FlatResidentSurvey;
  };
}>;

/**
 * Get Contact
 */

export class BlockManagerGetContactParams extends BlockManagerContactParams {}

export type BlockManagerGetContactRequest = GetEndpoint<{
  url: '/blockmanagement/properties/:propertyUid/flats/:flatUid/contacts/:contactUid';
  params: typeof BlockManagerGetContactParams;
  query: {};
  response: {
    contact: EngageContact;
  };
}>;

export class BlockManagerListContactsQuery {
  @IsOptional()
  @IsIn(['active', 'archived', 'both'])
  state?: 'active' | 'archived' | 'both';
}

export type BlockManagerListContactsRequest = GetEndpoint<{
  url: '/blockmanagement/properties/:propertyUid/flats/:flatUid/contacts';
  params: typeof BlockManagerFlatParams;
  query: BlockManagerListContactsQuery;
  response: {
    contacts: EngageContact[];
  };
}>;

/**
 * Create Flat
 */

export class BlockManagerLinkFlatElementParams {
  @IsCuid()
  @IsDefined()
  element_uid!: string;
}

type BlockManagerLinkFlatElementResponse = {
  message: string;
  flatElement: {
    propertyId: number; // ID of property in RiskBase
    assetId: number; // ID of flat in RiskBase
    groupId: number; // ID of field group that assetId is in
    organisationId: number;
    flatId: number | null;
    flatUid: string | null;
  };
  linkId?: number;
  created: boolean;
};

export type BlockManagerLinkFlatElementRequest = PutEndpoint<{
  url: '/blockmanagement/create-flat-element/:element_uid';
  params: typeof BlockManagerLinkFlatElementParams;
  body: {};
  query: {};
  response: BlockManagerLinkFlatElementResponse;
}>;

/**
 * Update Flat
 */

export class BlockManagerUpdateFlatParams {
  @IsCuid()
  @IsDefined({ message: 'propertyUid is required.' })
  propertyUid!: string;

  @IsCuid()
  @IsDefined({ message: 'flatUid is required.' })
  flatUid!: string;
}

export class BlockManagerUpdateFlatBody {
  constructor(data: BlockManagerUpdateFlatBody) {
    Object.assign(this, data);
  }

  @IsOptional()
  @IsEnum(FlatOccupationStatus)
  occupationStatus?: FlatOccupationStatus;

  @IsOptional()
  @IsEnum(EngageFlatIsHMO)
  isHMO?: EngageFlatIsHMO;

  @IsOptional()
  @IsEnum(EngageFlatIsCommerciallyOwned)
  isCommerciallyOwned?: EngageFlatIsCommerciallyOwned;

  @IsOptional()
  @IsBoolean()
  isMissingResidents?: boolean;
}

type BlockManagerUpdateFlatResponse = {
  updatedContacts: EngageContact[];
  archivedContactIds: string[];
  isHMO: EngageFlatIsHMO;
  isCommerciallyOwned: EngageFlatIsCommerciallyOwned;
  residentSurvey?: FlatResidentSurvey;
};

export type BlockManagerUpdateFlatRequest = PutEndpoint<{
  url: '/blockmanagement/properties/:propertyUid/flats/:flatUid';
  params: typeof BlockManagerUpdateFlatParams;
  body: typeof BlockManagerUpdateFlatBody;
  query: {};
  response: BlockManagerUpdateFlatResponse;
}>;
